<script>
import { ref, computed, watch } from '@vue/composition-api';
import {
  BModal,
  BFormFile,
  BAlert,
  BTable,
  BProgress,
  BBadge,
  BSpinner,
  BButton,
} from 'bootstrap-vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import * as XLSX from 'xlsx';
import store from '@/store';
import moment from 'moment';

export default {
  name: 'InvoiceListImportModal',
  components: {
    BModal,
    BFormFile,
    BAlert,
    BTable,
    BProgress,
    FeatherIcon,
    BBadge,
    BSpinner,
    BButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['bulkImport', 'update:show'],
  setup(props, { emit }) {
    const file = ref(null);
    const processing = ref(false);
    const fileProcessing = ref(false);
    const validationErrors = ref([]);
    const previewData = ref([]);
    const progressValue = ref(0);
    const progressText = ref('');

    // Template URL - Updated for new API structure
    // const templateUrl = '/downloads/SalesInvoiceFBR_BulkUpload_Template.xlsx';

    // Function to generate and download template dynamically
    const downloadTemplate = () => {
      // Define headers
      const headers = [
        'Document Number*',
        'Transaction Date*',
        'NTN',
        'CNIC',
        'Sales Type Code*',
        'Item Code*',
        'Reference No',
        'Quantity*',
        'Unit of Measurement',
        'HS Code*',
        'Rate*',
        'Value',
        'Discount %',
        'Sales Tax %',
        'Fixed/Notified',
        'Further Tax %',
        'Additional Sales Tax %',
        '236G/H %',
        '236G/H Amount',
        'Sales Tax Invoice No',
        'SRO Schedule No',
        'SRO Item Serial No',
        'Remarks',
        'FBR Invoice No',
        'FBR Note'
      ];
      
      // Create a sample row
      const sampleRow = [
        'INV-1001',             // Document Number
        moment().format('YYYY-MM-DD'), // Transaction Date
        '1234567-8',            // NTN
        '12345-1234567-1',      // CNIC
        'ST-01',                // Sales Type Code
        'ITEM-001',             // Item Code
        'REF-001',              // Reference No
        10,                     // Quantity
        'Nos',                  // Unit
        '1234.5678',            // HS Code
        100,                    // Rate
        1000,                   // Value (Qty * Rate)
        0,                      // Discount %
        18,                     // Sales Tax %
        0,                      // Fixed/Notified
        0,                      // Further Tax %
        0,                      // Additional Sales Tax %
        0,                      // 236G/H %
        0,                      // 236G/H Amount
        '32424',                // Sales Tax Invoice No
        '297(I)/2023-Table-II', // SRO Schedule No
        '2',                    // SRO Item Serial No
        'Sample Invoice',       // Remarks
        '',                     // FBR Invoice No
        ''                      // FBR Note
      ];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      
      // Combine headers and sample row
      const wsData = [headers, sampleRow];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
          { wch: 20 }, // Document Number
          { wch: 15 }, // Transaction Date
          { wch: 15 }, // NTN
          { wch: 18 }, // CNIC
          { wch: 15 }, // Sales Type Code
          { wch: 15 }, // Item Code
          { wch: 15 }, // Reference No
          { wch: 10 }, // Quantity
          { wch: 15 }, // Unit
          { wch: 15 }, // HS Code
          { wch: 10 }, // Rate
          { wch: 15 }, // Value
          { wch: 12 }, // Discount %
          { wch: 12 }, // Sales Tax %
          { wch: 15 }, // Fixed/Notified
          { wch: 15 }, // Further Tax %
          { wch: 15 }, // Add Sales Tax %
          { wch: 15 }, // 236G/H %
          { wch: 15 }, // 236G/H Amount
          { wch: 20 }, // Sales Tax Invoice No
          { wch: 20 }, // SRO Schedule No
          { wch: 20 }, // SRO Item Serial No
          { wch: 20 }, // Remarks
          { wch: 15 }, // FBR Invoice No
          { wch: 20 }, // FBR Note
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

      // Generate file and trigger download
      XLSX.writeFile(wb, 'SalesInvoice_Template.xlsx');
    };

    // Preview table fields - showing all columns
    const previewFields = [
      { key: 'docNo', label: 'Doc No', sortable: true },
      { key: 'tranDate', label: 'Date', sortable: true },
      { key: 'ntnNumber', label: 'NTN', sortable: true },
      { key: 'cnicNumber', label: 'CNIC', sortable: true },
      { key: 'salesTypeCode', label: 'Sales Type', sortable: true },
      { key: 'salesTaxInvoiceNo', label: 'ST Inv No', sortable: true },
      { key: 'sroScheduleNo', label: 'SRO Schedule No', sortable: true },
      { key: 'remarks', label: 'Remarks' },
      { key: 'lineItems', label: 'Items', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
      { key: 'grossAmount', label: 'Gross', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'totalDiscount', label: 'Discount', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'valWithoutTax', label: 'Value w/o Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'totalSalesTax', label: 'Sales Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'notifiedValue', label: 'Fixed/Notified', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'totalFurtherTax', label: 'Further Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'totalAdvanceTax', label: 'Add. Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'valIncludingTax', label: '236G Amount', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'netAmount', label: 'Net Amount', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
    ];

    // Computed property for modal visibility
    const showModal = computed({
      get: () => props.show,
      set: (value) => emit('update:show', value),
    });

    const findColumnIndex = (headers, names) => {
      return headers.findIndex((header) => {
        if (!header) return false;
        const normalizedHeader = header.toString().trim().toLowerCase();
        return names.some((name) => normalizedHeader === name || normalizedHeader.includes(name));
      });
    };

    // Format import field — empty values become '-'
    const formatImportFieldValue = (value) => {
      const trimmed = value !== null && value !== undefined ? value.toString().trim() : '';
      return trimmed || '-';
    };

    // Add lineItems computed property to preview data
    const processedPreviewData = computed(() => {
      return previewData.value.map((invoice) => {
        const hasDate = !!invoice.Entity.tranDate;
        const ntnNumber = formatImportFieldValue(invoice.Entity.ntnNumber);
        const cnicNumber = formatImportFieldValue(invoice.Entity.cnicNumber);
        const hasCustomer = ntnNumber !== '-' || cnicNumber !== '-';
        const hasSTInvoice = !!invoice.Entity.salesTaxInvoiceNo && invoice.Entity.salesTaxInvoiceNo !== '-';
        const hasItems = invoice.Details.length > 0;
        
        // Logical "Valid" means it has the absolute minimum to be imported (Date + Items)
        const isValid = hasDate && hasItems;
        // Logical "Complete" means it has optional but critical fields like Customer/ST Inv No
        const isFullyComplete = isValid && hasCustomer && hasSTInvoice;

        return {
          docNo: invoice.Entity.docNo,
          ntnNumber,
          cnicNumber,
          tranDate: invoice.Entity.tranDate
            ? moment(invoice.Entity.tranDate).format('DD/MM/YYYY')
            : null,
          hasDate,
          hasCustomer,
          hasSTInvoice,
          hasItems,
          isValid,
          isFullyComplete,
          salesTypeCode: invoice.Entity.salesTypeCode,
          salesTaxInvoiceNo: invoice.Entity.salesTaxInvoiceNo || '-',
          sroScheduleNo: invoice.Entity.sroScheduleNo || '-',
          remarks: invoice.Entity.remarks || '-',
          lineItems: invoice.Details.length,
          grossAmount: invoice.Entity.grossAmount,
          totalDiscount: invoice.Entity.totalDiscount,
          valWithoutTax: invoice.Entity.valWithoutTax,
          totalSalesTax: invoice.Entity.totalSalesTax,
          notifiedValue: invoice.Entity.notifiedValue,
          totalFurtherTax: invoice.Entity.totalFurtherTax,
          totalAdvanceTax: invoice.Entity.totalAdvanceTax,
          valIncludingTax: invoice.Entity.valIncludingTax,
          netAmount: invoice.Entity.netAmount,
        };
      });
    });

    // Calculate totals for summary
    const totalLineItems = computed(() => {
      return previewData.value.reduce((total, invoice) => total + invoice.Details.length, 0);
    });

    const totalAmount = computed(() => {
      return previewData.value.reduce((total, invoice) => total + invoice.Entity.grossAmount, 0);
    });

    // Watch for file changes to process and validate
    watch(file, async (newFile) => {
      if (newFile) {
        fileProcessing.value = true;
        validationErrors.value = [];
        previewData.value = [];

        try {
          await processExcelFile(newFile);
        } finally {
          fileProcessing.value = false;
        }
      } else {
        previewData.value = [];
        validationErrors.value = [];
      }
    });

    // Process Excel file
    const processExcelFile = async (excelFile) => {
      try {
        const data = await readExcelFile(excelFile);

        if (!data || data.length === 0) {
          validationErrors.value = ['Excel file appears to be empty or invalid.'];
          return;
        }

        console.log('Raw Excel data:', data);
        console.log('Headers:', data[0]);
        console.log('First data row:', data[1]);

        const { invoices, errors } = parseExcelData(data);

        validationErrors.value = errors;
        previewData.value = invoices;

        if (errors.length === 0) {
          console.log('Parsed invoices with new API structure:', invoices);
          console.log('API request body will be:', JSON.stringify(invoices, null, 2));
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        validationErrors.value = [
          "Error reading Excel file. Please ensure it's a valid Excel file.",
          'Make sure the file follows the template format exactly.',
        ];
      }
    };

    // Read Excel file
    const readExcelFile = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            console.log('Reading Excel file with options...');

            const workbook = XLSX.read(data, {
              type: 'array',
              cellDates: false, // Don't parse dates as Date objects automatically to avoid timezone shifts
              cellNF: false, // Don't parse number formats
              cellText: false, // Don't parse text
            });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            console.log('Worksheet info:', {
              sheetName,
              range: worksheet['!ref'],
              cellDates: true,
            });

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true, // Keep raw native values instead of converting to localized strings
              dateNF: 'yyyy-mm-dd', // Date format
            });

            console.log('Excel reading completed');
            resolve(jsonData);
          } catch (error) {
            console.error('Error reading Excel file:', error);
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    };

    // Parse Excel data and group by document number
    const parseExcelData = (excelData) => {
      if (excelData.length < 2) {
        return {
          invoices: [],
          errors: ['Excel file must contain at least a header row and one data row.'],
        };
      }

      const headers = excelData[0];
      const dataRows = excelData.slice(1);
      const errors = [];
      const invoiceMap = new Map();

      // Find required column indices
      const requiredColumns = {
        docNo: headers.findIndex((h) => h && h.toString().includes('Document Number*')),
        tranDate: headers.findIndex((h) => h && h.toString().includes('Transaction Date*')),
        ntnNumber: findColumnIndex(headers, ['ntn number', 'ntn no', 'ntn no.', 'ntn']),
        cnicNumber: findColumnIndex(headers, ['cnic number', 'cnic no', 'cnic no.', 'cnic']),
        salesTypeCode: headers.findIndex((h) => h && h.toString().includes('Sales Type Code*')),
        itemCode: headers.findIndex((h) => h && h.toString().includes('Item Code*')),
        qty: headers.findIndex((h) => h && h.toString().includes('Quantity*')),
        unitName: headers.findIndex((h) => h && h.toString().includes('Unit of Measurement')),
        hsCode: headers.findIndex((h) => h && h.toString().includes('HS Code*')),
        rate: headers.findIndex((h) => h && h.toString().includes('Rate*')),
        discountPercent: headers.findIndex((h) => h && h.toString().includes('Discount %')),
        salesTaxPercent: headers.findIndex((h) => h && h.toString().includes('Sales Tax %')),
        notifiedValue: headers.findIndex((h) => h && h.toString().includes('Fixed/Notified')),
        furtherTaxPercent: headers.findIndex((h) => h && h.toString().includes('Further Tax %')),
        addSalesTaxPercent: headers.findIndex(
          (h) => h && h.toString().includes('Additional Sales Tax %')
        ),
        salesTaxInvoiceNo: headers.findIndex((h) => h && h.toString().includes('Sales Tax Invoice No')),
        sroScheduleNo: headers.findIndex((h) => h && h.toString().includes('SRO Schedule No')),
        sroItemSerialNo: headers.findIndex((h) => h && h.toString().includes('SRO Item Serial No')),
        value: headers.findIndex((h) => h && (h.toString().includes('Value') || h.toString().includes('Amount'))),
        tax236GValue: headers.findIndex((h) => h && h.toString().includes('236G/H %')),
        valIncludingTax: headers.findIndex((h) => h && h.toString().includes('236G/H Amount')),
      };

      console.log('Required columns found:', requiredColumns);
      console.log('Transaction Date column index:', requiredColumns.tranDate);
      console.log('Transaction Date header:', headers[requiredColumns.tranDate]);

      // Validate required columns exist
      const missingColumns = Object.entries(requiredColumns)
        .filter(([key, index]) => index === -1)
        .map(([key]) => key);

      if (missingColumns.length > 0) {
        errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
        errors.push('Please ensure your Excel file follows the new template format exactly.');
        return { invoices: [], errors };
      }

      // Process each row
      dataRows.forEach((row, rowIndex) => {
        if (
          row.length === 0 ||
          !row.some((cell) => cell !== null && cell !== undefined && cell !== '')
        ) {
          return; // Skip empty rows
        }

        // Safe parser that strips commas
        const safelyParseNumber = (val) => {
          if (val === null || val === undefined || val === '') return 0;
          if (typeof val === 'number') return val;
          const cleaned = val.toString().replace(/,/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : parsed;
        };

        const docNo = row[requiredColumns.docNo];
        if (!docNo) {
          errors.push(`Row ${rowIndex + 2}: Document Number is required`);
          return;
        }

        const salesTypeCode = row[requiredColumns.salesTypeCode];
        if (!salesTypeCode) {
          errors.push(`Row ${rowIndex + 2}: Sales Type Code is required`);
          return;
        }

        // Validate required fields for each row
        const itemCode = row[requiredColumns.itemCode];
        if (!itemCode) {
          errors.push(`Row ${rowIndex + 2}: Item Code is required`);
          return;
        }

        const qty = safelyParseNumber(row[requiredColumns.qty]);
        if (qty <= 0) {
          errors.push(`Row ${rowIndex + 2}: Quantity must be a positive number`);
          return;
        }

        const rate = safelyParseNumber(row[requiredColumns.rate]);
        if (rate < 0) {
          errors.push(`Row ${rowIndex + 2}: Rate must be a non-negative number`);
          return;
        }

        const unitName = row[requiredColumns.unitName];
        if (!unitName) {
          errors.push(`Row ${rowIndex + 2}: Unit of Measurement is required`);
          return;
        }

        const hsCode = row[requiredColumns.hsCode];
        if (!hsCode) {
          errors.push(`Row ${rowIndex + 2}: HS Code is required`);
          return;
        }

        // Create or get invoice
        if (!invoiceMap.has(docNo)) {
          const rawDateValue = row[requiredColumns.tranDate];
          console.log('Raw date value from Excel:', rawDateValue, 'type:', typeof rawDateValue);

          const tranDate = formatDate(rawDateValue);
          
          // Add validation error if date is missing
          if (!tranDate) {
            errors.push(`Doc No ${docNo}: Transaction Date is missing or invalid. Please check your Excel file.`);
          }

          console.log('Date parsing debug:', {
            originalValue: rawDateValue,
            type: typeof rawDateValue,
            parsedDate: tranDate,
          });

          invoiceMap.set(docNo, {
            Entity: {
              businessID: 0, // Will be set from store
              docNo: docNo.toString().trim(),
              ntnNumber: formatImportFieldValue(
                requiredColumns.ntnNumber !== -1 ? row[requiredColumns.ntnNumber] : ''
              ),
              cnicNumber: formatImportFieldValue(
                requiredColumns.cnicNumber !== -1 ? row[requiredColumns.cnicNumber] : ''
              ),
              salesTypeCode: row[requiredColumns.salesTypeCode]?.toString().trim() || '',
              tranDate: tranDate,
              remarks: formatImportFieldValue(
                row[headers.findIndex((h) => h && h.toString().includes('Remarks'))]
              ),
              salesTaxInvoiceNo: 
                requiredColumns.salesTaxInvoiceNo !== -1 
                  ? formatImportFieldValue(row[requiredColumns.salesTaxInvoiceNo]) : '-',
              sroScheduleNo:
                requiredColumns.sroScheduleNo !== -1 
                  ? formatImportFieldValue(row[requiredColumns.sroScheduleNo]) : '-',
              fbrInvoiceNo: formatImportFieldValue(
                row[headers.findIndex((h) => h && h.toString().includes('FBR Invoice No'))]
              ),
              fbrNote: formatImportFieldValue(
                row[headers.findIndex((h) => h && h.toString().includes('FBR Note'))]
              ),
              grossAmount: 0,
              totalSalesTax: 0,
              notifiedValue: 0,
              totalFurtherTax: 0,
              totalAdvanceTax: 0,
              totalDiscount: 0,
              netAmount: 0,
              totalTax: 0,
              valWithoutTax: 0,
              valIncludingTax: 0,
              tax236GValue: 0,
            },
            Details: [],
          });
        }

        const invoice = invoiceMap.get(docNo);

        // Helper function to normalize percentage values from Excel
        // Excel stores percentages in two ways:
        // 1. As decimal: 18% = 0.18 (when cell is formatted as percentage)
        // 2. As number: 18% = 18 or 18.0 (when cell is formatted as number or general)
        const normalizePercent = (value) => {
          // Handle empty/null/undefined
          if (value === null || value === undefined || value === '') {
            return 0;
          }
          
          // Parse to float and handle string commas securely
          const parsed = typeof value === 'number' ? value : parseFloat(value.toString().replace(/,/g, ''));
          
          // If parsing failed, return 0
          if (isNaN(parsed)) {
            return 0;
          }
          
          // If value is between 0 and 1 (like 0.18, 0.11, 0.10)
          // Excel formatted as percentage - convert to actual percentage number
          if (parsed > 0 && parsed < 1) {
            console.log(`Converting decimal ${parsed} to percentage ${parsed * 100}`);
            return parsed * 100;
          }
          
          // If value is 1 or greater (like 18, 11, 10), it's already a percentage number
          console.log(`Value ${parsed} is already in percentage format`);
          return parsed;
        };

        // Create line item with normalized percentage values
        const lineItem = {
          itemCode: itemCode.toString().trim(),
          refNo:
            row[headers.findIndex((h) => h && h.toString().includes('Reference No'))]?.toString().trim() ||
            '',
          qty: qty,
          unitName: unitName.toString().trim(),
          hsCode: hsCode.toString().trim(),
          rate: rate,
          discountPercent: normalizePercent(row[requiredColumns.discountPercent]),
          salesTaxPercent: normalizePercent(row[requiredColumns.salesTaxPercent]),
          notifiedValue: safelyParseNumber(row[requiredColumns.notifiedValue]),
          furtherTaxPercent: normalizePercent(row[requiredColumns.furtherTaxPercent]),
          addSalesTaxPercent: normalizePercent(row[requiredColumns.addSalesTaxPercent]),
          sroScheduleNo: requiredColumns.sroScheduleNo !== -1 ? row[requiredColumns.sroScheduleNo]?.toString().trim() || '' : '',
          sroItemSerialNo: requiredColumns.sroItemSerialNo !== -1 ? row[requiredColumns.sroItemSerialNo]?.toString().trim() || '' : '',
          value: requiredColumns.value !== -1 ? safelyParseNumber(row[requiredColumns.value]) : 0,
          tax236GValue: requiredColumns.tax236GValue !== -1 ? normalizePercent(row[requiredColumns.tax236GValue]) : 0,
          valIncludingTax: requiredColumns.valIncludingTax !== -1 ? safelyParseNumber(row[requiredColumns.valIncludingTax]) : 0,
        };

        invoice.Details.push(lineItem);
      });

      // Calculate invoice totals
      const invoices = Array.from(invoiceMap.values()).map((invoice) => {
        const totals = invoice.Details.reduce(
          (acc, item, itemIndex) => {
            // Use provided value/amount if available, otherwise calculate from qty * rate
            const grossAmount = item.value ? item.value : (item.qty * item.rate);
            
            // Calculate discount based on gross amount (either provided or calculated)
            const discountAmount = (grossAmount * item.discountPercent) / 100;
            const netAmount = grossAmount - discountAmount;
            const salesTaxAmount = (netAmount * item.salesTaxPercent) / 100;
            const furtherTaxAmount = (netAmount * item.furtherTaxPercent) / 100;
            const addSalesTaxAmount = (netAmount * item.addSalesTaxPercent) / 100;
            const totalTax = salesTaxAmount + furtherTaxAmount + addSalesTaxAmount;
            
            // Allow manual override from excel for 236G, otherwise compute
            const baseAmountFor236 = netAmount + totalTax;
            const tax236GValue = item.tax236GValue || 0;
            const valIncludingTax = item.valIncludingTax ? item.valIncludingTax : ((baseAmountFor236 * tax236GValue) / 100);

            acc.grossAmount += grossAmount;
            acc.totalSalesTax += salesTaxAmount;
            acc.notifiedValue += (item.notifiedValue || 0);
            acc.totalFurtherTax += furtherTaxAmount;
            acc.totalAdvanceTax += addSalesTaxAmount;
            acc.totalDiscount += discountAmount;
            acc.totalTax += totalTax;
            acc.valWithoutTax += netAmount;
            acc.valIncludingTax += valIncludingTax;
            acc.tax236GValue += tax236GValue;
            return acc;
          },
          {
            grossAmount: 0,
            totalSalesTax: 0,
            notifiedValue: 0,
            totalFurtherTax: 0,
            totalAdvanceTax: 0,
            totalDiscount: 0,
            totalTax: 0,
            valWithoutTax: 0,
            valIncludingTax: 0,
            tax236GValue: 0,
          }
        );

        invoice.Entity.grossAmount = totals.grossAmount;
        invoice.Entity.totalSalesTax = totals.totalSalesTax;
        invoice.Entity.notifiedValue = totals.notifiedValue;
        invoice.Entity.totalFurtherTax = totals.totalFurtherTax;
        invoice.Entity.totalAdvanceTax = totals.totalAdvanceTax;
        invoice.Entity.totalDiscount = totals.totalDiscount;
        invoice.Entity.totalTax = totals.totalTax;
        invoice.Entity.valWithoutTax = totals.valWithoutTax;
        invoice.Entity.valIncludingTax = totals.valIncludingTax;
        invoice.Entity.tax236GValue = totals.tax236GValue;
        invoice.Entity.netAmount = totals.valWithoutTax + totals.totalTax + totals.valIncludingTax;

        return invoice;
      });

      // Validate that we have at least one invoice with line items
      if (invoices.length === 0) {
        errors.push('No valid invoices found in the Excel file');
        return { invoices, errors };
      }

      // Validate that each invoice has at least one line item
      const invoicesWithoutItems = invoices.filter((invoice) => invoice.Details.length === 0);
      if (invoicesWithoutItems.length > 0) {
        errors.push(
          `Invoices without line items: ${invoicesWithoutItems.map((inv) => inv.Entity.docNo).join(', ')}`
        );
        return { invoices, errors };
      }

      return { invoices, errors };
    };

    // Convert Excel serial number to date
    const excelSerialToDate = (serial) => {
      // Excel stores dates as serial numbers.
      // The offset 25569 is for 1970-01-01.
      // We use Math.floor to ensure we only get the date part if there's a time fractional.
      const date = new Date(Math.round((serial - 25569) * 86400 * 1000));
      return date;
    };

    // Parse various date formats
    const parseDateString = (dateString) => {
      if (!dateString || typeof dateString !== 'string') return null;

      // Remove any extra whitespace
      dateString = dateString.trim();

      // Try common date formats
      const dateFormats = [
        // MM/DD/YYYY
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString),
        // DD/MM/YYYY
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString),
        // YYYY-MM-DD
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(dateString),
        // MM-DD-YYYY
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateString),
        // DD-MM-YYYY
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateString),
      ];

      for (let i = 0; i < dateFormats.length; i++) {
        const match = dateFormats[i];
        if (match) {
          let year, month, day;

          if (i === 0 || i === 3) {
            // MM/DD/YYYY or MM-DD-YYYY
            month = parseInt(match[1]) - 1;
            day = parseInt(match[2]);
            year = parseInt(match[3]);
          } else if (i === 1 || i === 4) {
            // DD/MM/YYYY or DD-MM-YYYY
            day = parseInt(match[1]);
            month = parseInt(match[2]) - 1;
            year = parseInt(match[3]);
          } else if (i === 2) {
            // YYYY-MM-DD
            year = parseInt(match[1]);
            month = parseInt(match[2]) - 1;
            day = parseInt(match[3]);
          }

          const date = new Date(year, month, day);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }

      return null;
    };

    // Handle Excel specific date formats
    // NOTE: Uses UTC-based formatting to avoid timezone shift (off-by-1 day bug)
    const parseExcelDate = (value) => {
      console.log('parseExcelDate called with:', value, 'type:', typeof value);

      // If it's a number, it's likely an Excel serial number
      // If it's a number, it's an Excel serial number
      if (typeof value === 'number') {
        console.log('Number detected, treating as Excel serial number');
        try {
          const date = excelSerialToDate(value);
          // Use moment.utc to format it correctly as a date string
          // This ensures timezone immunity (46083 always becomes March 2nd)
          const result = moment.utc(date).format('YYYY-MM-DD');
          console.log('Converted Excel serial to date (UTC):', result);
          return result;
        } catch (error) {
          console.error('Error converting Excel serial to date:', error);
          return null;
        }
      }

      // If it's already a Date object (cellDates:true gives Date objects)
      if (value instanceof Date) {
        console.log('Date object detected, formatting as local YYYY-MM-DD');
        // Use moment or local getters to avoid the UTC off-by-one shift
        // If the date is 2026-03-02 00:00:00 Local, we want "2026-03-02"
        return moment(value).format('YYYY-MM-DD');
      }

      // If it's a string, try to parse it
      if (typeof value === 'string') {
        console.log('String detected, trying to parse as Excel date');

        // Try custom parser first
        const parsedDate = parseDateString(value);
        if (parsedDate) {
          const yyyy = parsedDate.getFullYear();
          const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
          const dd = String(parsedDate.getDate()).padStart(2, '0');
          const result = `${yyyy}-${mm}-${dd}`;
          console.log('Successfully parsed with custom parser (UTC-safe):', result);
          return result;
        }

        // Try native Date constructor as last resort
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          console.log('Successfully parsed with native parser:', moment(date).format('YYYY-MM-DD'));
          return moment(date).format('YYYY-MM-DD');
        }

        console.log('Could not parse string as date:', value);
        return null;
      }

      console.log('Unsupported date type:', typeof value);
      return null;
    };

    // Format date for API — returns null if no date provided (no silent fallback to today)
    const formatDate = (dateValue) => {
      console.log('formatDate called with:', dateValue, 'type:', typeof dateValue);

      if (dateValue === null || dateValue === undefined || dateValue === '') {
        console.log('No date value provided — will be flagged as missing in preview');
        return null; // Return null so the UI can flag missing dates
      }

      // Use the new Excel date parser
      const parsedDate = parseExcelDate(dateValue);
      if (parsedDate) {
        return parsedDate;
      }

      // If all else fails, return null (do NOT silently use today's date)
      console.warn('Could not parse date value:', dateValue, '— flagging as missing');
      return null;
    };

    // Format currency for display
    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    // Submit for import
    const onSubmit = async () => {
      if (!file.value || previewData.value.length === 0) {
        return;
      }

      // Check if businessID is available
      const businessID = store.state.user.businessID;
      if (!businessID) {
        validationErrors.value = [
          'Business ID not found. Please ensure you are logged in with a valid business account.',
        ];
        return;
      }

      processing.value = true;
      progressValue.value = 0;
      progressText.value = 'Preparing data for import...';

      try {
        // Set businessID for all invoices and line items
        const processedData = previewData.value.map((invoice) => ({
          Entity: { ...invoice.Entity, businessID },
          Details: invoice.Details.map((detail) => ({ ...detail, businessID })),
        }));

        progressValue.value = 50;
        progressText.value = 'Uploading to server...';

        // Emit the bulk import event with a completion callback
        emit('bulkImport', processedData, (success, response) => {
          console.log('Import callback received:', { success, response });
          
          const responseData = response?.data || {};
          const innerData = responseData?.data || {};
          
          // Check if there are validation errors even if status is "success"
          const hasValidationErrors = innerData?.validationErrors && 
                                      innerData.validationErrors.length > 0;
          const errorCount = innerData?.errorCount || 0;
          
          if (success && !hasValidationErrors && errorCount === 0 && responseData.status !== 'error') {
            // True success - no errors
            progressValue.value = 100;
            progressText.value = 'Import completed successfully!';

            // Reset form
            file.value = null;
            previewData.value = [];
            validationErrors.value = [];

            // Close modal after showing success message
            setTimeout(() => {
              showModal.value = false;
            }, 1500);
          } else {
            // Error or validation errors present - show detailed error message
            console.error('API Response with errors:', response);
            
            const errors = [];
            
            // Add main message
            if (responseData?.message) {
              errors.push(responseData.message);
            } else if (response?.message) {
              errors.push(response.message);
            }
            
            // Add validation errors from API response
            if (innerData?.validationErrors && Array.isArray(innerData.validationErrors)) {
              if (innerData.validationErrors.length > 0) {
                errors.push('');
                errors.push('❌ Validation Errors:');
                innerData.validationErrors.forEach((err, index) => {
                  errors.push(`${index + 1}. ${err}`);
                });
              }
            }
            
            // Add error summary if available
            if (innerData?.successCount !== undefined && innerData?.errorCount !== undefined) {
              errors.push('');
              errors.push(`📊 Summary: ${innerData.successCount} invoices uploaded successfully, ${innerData.errorCount} failed`);
            }
            
            // If no errors were collected, add a generic message
            if (errors.length === 0) {
              errors.push('Import failed: Unknown error occurred');
            }
            
            validationErrors.value = errors;
            progressValue.value = 0;
            progressText.value = 'Import completed with errors. Please check the details above.';
          }
          processing.value = false;
        });
      } catch (error) {
        console.error('Import error:', error);
        validationErrors.value = [`Import failed: ${error.message}`];
        progressValue.value = 0;
        progressText.value = 'Import failed. Please check the errors above.';
        processing.value = false;
      }
    };

    // Cancel import
    const onCancel = () => {
      if (processing.value) {
        // Don't allow cancellation during processing
        return;
      }

      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
      showModal.value = false;
    };

    // Hide modal
    const onHide = () => {
      if (processing.value) {
        // Don't allow closing during processing
        return false;
      }

      // Reset form when closing
      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
      return true;
    };

    // Clear form
    const clearForm = () => {
      if (processing.value) {
        // Don't allow clearing during processing
        return;
      }

      file.value = null;
      previewData.value = [];
      validationErrors.value = [];
    };

    return {
      file,
      processing,
      fileProcessing,
      validationErrors,
      previewData,
      processedPreviewData,
      totalLineItems,
      totalAmount,
      progressValue,
      progressText,
      // templateUrl,
      previewFields,
      showModal,
      onSubmit,
      onCancel,
      onHide,
      onCancel,
      onHide,
      formatCurrency,
      clearForm,
      parseExcelDate,
      parseDateString,
      excelSerialToDate,
      downloadTemplate,
    };
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    title="Import Sales Invoices"
    size="xl"
    :ok-disabled="!file || processing || validationErrors.length > 0 || previewData.length === 0"
    :cancel-disabled="processing"
    :hide-header-close="processing"
    @ok="onSubmit"
    @cancel="onCancel"
    @hide="onHide"
    :before-close="onHide"
  >
    <div class="mb-3">
      <p class="text-muted mb-2">
        Please upload an Excel file following the template format. Required fields are marked with
        *. NTN and CNIC are optional; empty values will be sent as "-".
        <br />
        <span class="text-primary font-weight-bold">
          Note: Please use date format DD/MM/YYYY or YYYY-MM-DD in the Excel file for Transaction Date.
        </span>
      </p>
      <button @click="downloadTemplate" class="btn btn-outline-primary btn-sm">
        <feather-icon icon="DownloadIcon" class="mr-1" />
        Download Template
      </button>
    </div>

    <b-form-file
      v-model="file"
      :state="Boolean(file)"
      placeholder="Choose an Excel file or drop it here..."
      drop-placeholder="Drop Excel file here..."
      drop-variant="primary"
      accept=".xlsx,.xls"
      :disabled="processing || fileProcessing"
    />

    <!-- File Processing Indicator -->
    <div v-if="fileProcessing" class="mt-3">
      <div class="text-center">
        <b-spinner small class="mr-2" />
        <span class="text-muted">Processing Excel file...</span>
      </div>
    </div>

    <!-- Validation Errors -->
    <div v-if="validationErrors.length > 0" class="mt-3">
      <b-alert variant="danger" show>
        <div class="d-flex justify-content-between align-items-start p-2">
          <div>
            <h6>Validation Errors:</h6>
            <ul class="mb-0">
              <li v-for="(error, index) in validationErrors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
          <b-button variant="outline-danger" size="sm" @click="clearForm" :disabled="processing">
            Clear & Try Again
          </b-button>
        </div>
      </b-alert>
    </div>

    <!-- Preview Data -->
    <div v-if="previewData.length > 0" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">
          <feather-icon icon="EyeIcon" class="mr-50" />
          Preview ({{ previewData.length }} invoices)
        </h6>
        <div class="d-flex align-items-center">
          <b-badge variant="light-success" class="mr-1">
            <feather-icon icon="CheckIcon" size="12" class="mr-25" />
            {{ processedPreviewData.filter(i => i.isValid).length }} Ready to Import
          </b-badge>
          <b-badge :variant="processedPreviewData.filter(i => !i.isValid || !i.isFullyComplete).length > 0 ? 'warning' : 'light-secondary'" class="mr-1">
            <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
            {{ processedPreviewData.filter(i => !i.isValid || !i.isFullyComplete).length }} Incomplete / Invalid
          </b-badge>
          <div class="text-muted border-left pl-1">
            <small class="font-weight-bold">
              Items: {{ totalLineItems }} | Total: {{ formatCurrency(totalAmount) }}
            </small>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <b-table
          :key="`invoice-import-preview-${previewData.length}-${file ? file.name : 'empty'}`"
          :items="processedPreviewData"
          :fields="previewFields"
          small
          striped
          hover
          responsive
          class="mb-0"
          :busy="processing"
          :tbody-tr-class="(item) => {
            if (!item.isValid) return 'row-missing-date';
            if (!item.isFullyComplete) return 'row-incomplete';
            return 'row-has-date';
          }"
        >
          <template #table-busy>
            <div class="text-center">
              <b-spinner small class="mr-2" />
              <span class="text-muted">Processing...</span>
            </div>
          </template>
          <template #cell(docNo)="data">
            <strong>{{ data.value }}</strong>
          </template>
          <template #cell(tranDate)="data">
            <span v-if="data.item.hasDate" class="text-dark">{{ data.value }}</span>
            <span v-else class="missing-date-cell">
              <feather-icon icon="AlertCircleIcon" class="mr-1" style="color:#dc3545;" size="14" />
              <span style="color:#dc3545; font-weight:600;">No Date</span>
            </span>
          </template>
          <template #cell(ntnNumber)="data">
            <span :class="data.value && data.value !== '-' ? 'text-primary' : 'text-muted'">{{ data.value }}</span>
          </template>
          <template #cell(cnicNumber)="data">
            <span :class="data.value && data.value !== '-' ? 'text-primary' : 'text-muted'">{{ data.value }}</span>
          </template>
          <template #cell(salesTypeCode)="data">
            <b-badge variant="light-primary">{{ data.value }}</b-badge>
          </template>
          <template #cell(remarks)="data">
            <span v-if="data.value && data.value !== '-'" class="text-truncate d-inline-block" style="max-width: 120px" :title="data.value">{{ data.value }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(lineItems)="data">
            <b-badge variant="info">{{ data.value }}</b-badge>
          </template>
          <template #cell(grossAmount)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(totalDiscount)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(valWithoutTax)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(totalSalesTax)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(notifiedValue)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(totalFurtherTax)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(totalAdvanceTax)="data">
            {{ formatCurrency(data.value) }}
          </template>
          <template #cell(netAmount)="data">
            <strong class="text-success">{{ formatCurrency(data.value) }}</strong>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Progress Bar -->
    <div v-if="processing" class="mt-3">
      <b-alert variant="info" show>
        <div class="d-flex align-items-center">
          <b-spinner small class="mr-2" />
          <span
            ><strong>Upload in Progress:</strong> Please wait while your invoices are being
            uploaded. The modal will close automatically when complete.</span
          >
        </div>
      </b-alert>
      <b-progress :value="progressValue" :max="100" animated />
      <small class="text-muted">{{ progressText }}</small>
    </div>

    <!-- Custom Footer -->
    <template #modal-footer>
      <div v-if="!processing" class="w-100 d-flex justify-content-end">
        <b-button variant="secondary" @click="onCancel" class="mr-2"> Cancel </b-button>
        <b-button
          variant="primary"
          @click="onSubmit"
          :disabled="!file || validationErrors.length > 0 || previewData.length === 0"
        >
          Import
        </b-button>
      </div>
      <div v-else class="w-100 text-center">
        <b-spinner small class="mr-2" />
        <span class="text-muted">Uploading invoices to server...</span>
      </div>
    </template>
  </b-modal>
</template>

<style lang="scss" scoped>
.table-responsive {
  max-height: 400px;
  overflow-x: auto;
  overflow-y: auto;
}

::v-deep .table {
  white-space: nowrap;
  min-width: 100%;
  
  th, td {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }
  
  th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 2;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
  }
  
  .text-truncate {
    max-width: 120px;
  }

  // Rows with a valid date: subtle green success tint
  tr.row-has-date > td {
    background-color: rgba(40, 167, 69, 0.04) !important;
    transition: background-color 0.2s ease;
  }
  
  tr.row-has-date:hover > td {
    background-color: rgba(40, 167, 69, 0.1) !important;
  }

  // Rows with a missing date or critical errors: shocking red warning tint
  tr.row-missing-date td {
    background-color: rgba(220, 53, 69, 0.12) !important;
    border-top: 1px solid rgba(220, 53, 69, 0.3) !important;
    border-bottom: 1px solid rgba(220, 53, 69, 0.3) !important;
    color: #b02a37 !important;
  }
  
  // Rows that are valid but missing optional info (Customer/ST Inv No): subtle amber tint
  tr.row-incomplete td {
    background-color: rgba(255, 159, 67, 0.08) !important;
    border-left: 5px solid #ff9f43 !important;
  }
  
  tr.row-missing-date td:first-child {
    border-left: 5px solid #dc3545 !important;
    font-weight: 700;
  }
  
  tr.row-missing-date:hover > td {
    background-color: rgba(220, 53, 69, 0.18) !important;
  }
}

// Missing date inline indicator
.missing-date-cell {
  display: inline-flex;
  align-items: center;
  background-color: #fff2f2;
  border: 1px solid #dc3545;
  border-radius: 4px;
  padding: 2px 8px;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.1);
}
</style>
